import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

// Single outer "liquid glass" panel (same blur/alpha/border treatment as
// MobilePillarExplorer in HealthCockpit.jsx) containing a stack of rows.
// Clicking a row expands it in place — title grows, full body + any extra
// content (lists, buttons, ...) fade in — while the others collapse to a
// title + one-line preview. framer-motion's `layout` prop on each row
// animates the resulting height changes, so rows smoothly move up/down as
// their siblings expand or collapse.
export default function WelcomeGlassAccordion({ items, initialSelected = 0 }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const reduceMotion = useReducedMotion();
  const [selected, setSelected] = useState(initialSelected);

  return (
    <Box
      sx={{
        borderRadius: '28px',
        overflow: 'hidden',
        bgcolor: dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
        boxShadow: dark
          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {items.map((item, i) => {
        const isOpen = i === selected;
        const bodyLines = Array.isArray(item.body) ? item.body : [item.body];

        return (
          <motion.div
            key={item.key ?? i}
            layout="position"
            transition={reduceMotion ? { duration: 0 } : { duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            style={{ borderRadius: 20 }}
          >
            <Box
              onClick={() => setSelected(i)}
              sx={{
                cursor: isOpen ? 'default' : 'pointer',
                borderRadius: '20px',
                p: isOpen ? 2.1 : 1.75,
                transition: 'padding 0.45s ease, background-color 0.45s ease, border-color 0.45s ease',
                ...(isOpen
                  ? {
                      bgcolor: dark ? alpha('#fff', 0.08) : alpha('#fff', 0.92),
                      border: `1px solid ${dark ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.95)'}`,
                      boxShadow: dark
                        ? 'inset 0 1px 0 rgba(255,255,255,0.14), 0 2px 14px rgba(0,0,0,0.4)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 2px 14px rgba(0,0,0,0.08)',
                    }
                  : {
                      border: '1px solid transparent',
                      '&:hover': { bgcolor: dark ? alpha('#fff', 0.03) : alpha('#fff', 0.35) },
                    }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: isOpen ? '1rem' : '0.92rem',
                    fontWeight: 700,
                    lineHeight: 1.3,
                    color: isOpen
                      ? (dark ? 'common.white' : 'greydark.C900')
                      : (dark ? 'grey.C500' : 'grey.C700'),
                    transition: 'font-size 0.45s ease, color 0.45s ease',
                  }}
                >
                  {item.title}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setSelected(isOpen ? null : i); }}
                  sx={{
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    color: isOpen
                      ? (dark ? 'common.white' : 'greydark.C900')
                      : (dark ? 'grey.C600' : 'grey.C500'),
                  }}
                >
                  <AnimatePresence initial={false} mode="wait">
                    <motion.span
                      key={isOpen ? 'minus' : 'plus'}
                      initial={reduceMotion ? {} : { opacity: 0, rotate: -45 }}
                      animate={{ opacity: 1, rotate: 0 }}
                      exit={reduceMotion ? {} : { opacity: 0, rotate: 45 }}
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                      style={{ display: 'flex' }}
                    >
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </motion.span>
                  </AnimatePresence>
                </IconButton>
              </Box>

              {!isOpen && bodyLines[0] && (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.8rem',
                    lineHeight: 1.5,
                    color: dark ? 'grey.C700' : 'grey.C500',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {bodyLines[0]}
                </Typography>
              )}

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={reduceMotion ? {} : { opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={reduceMotion ? {} : { opacity: 0, height: 0 }}
                    transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {bodyLines.map((line, li) => (
                        <Typography
                          key={li}
                          sx={{
                            fontSize: '0.9rem',
                            lineHeight: 1.7,
                            color: dark ? 'grey.C300' : 'text.secondary',
                          }}
                        >
                          {line}
                        </Typography>
                      ))}
                      {item.extra}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        );
      })}
    </Box>
  );
}
